import type { SearchParamsValuesType } from '../../../../constants';
import type { AdditionalFilterInDropdown } from '../../TableFilterBar/AdditionalFilters/constants';
import { isAdditionalFilterDropdown } from '../../TableFilterBar/AdditionalFilters/utils';
import type { TopBarFilter } from '../FilterValueChips';
import type { FilterAndGroup, FilterCondition, FilterRuleTree } from './types';
import { JunctionType } from './types';

const buildConditionFromTopBarFilter = (filter: TopBarFilter): FilterCondition => ({
  type: 'condition',
  filterId: filter.searchParam,
  label: filter.label,
  values: filter.values.map((v) => v.label),
});

const buildConditionFromDropdownFilter = (filter: AdditionalFilterInDropdown): FilterCondition | null => {
  if (!isAdditionalFilterDropdown(filter)) return null;

  const { selectedFilterValues } = filter;
  if (!selectedFilterValues || !Array.isArray(selectedFilterValues) || selectedFilterValues.length === 0) return null;

  const values = selectedFilterValues.map((v) => (typeof v === 'string' ? v : v.label));

  return {
    type: 'condition',
    filterId: filter.searchParam as string,
    label: filter.label,
    values,
  };
};

const buildConditionFromNonDropdownFilter = (
  filter: AdditionalFilterInDropdown,
  searchParam: SearchParamsValuesType
): FilterCondition | null => {
  if (isAdditionalFilterDropdown(filter)) return null;
  if (filter.selectedFilterValues == null) return null;

  return {
    type: 'condition',
    filterId: searchParam,
    label: filter.label,
    values: [String(filter.selectedFilterValues)],
  };
};

export const buildRuleTree = (
  topBarFilters: TopBarFilter[],
  selectedFilters: AdditionalFilterInDropdown[]
): FilterRuleTree => {
  const conditions: FilterCondition[] = [];

  topBarFilters.forEach((filter) => {
    if (filter.values.length > 0) {
      conditions.push(buildConditionFromTopBarFilter(filter));
    }
  });

  selectedFilters.forEach((filter) => {
    const condition = isAdditionalFilterDropdown(filter)
      ? buildConditionFromDropdownFilter(filter)
      : buildConditionFromNonDropdownFilter(filter, filter.searchParam);

    if (condition) {
      conditions.push(condition);
    }
  });

  const group: FilterAndGroup = {
    type: 'and',
    conditions,
  };

  return {
    junction: JunctionType.OR,
    groups: conditions.length > 0 ? [group] : [],
  };
};
